/**
 * SimuClinic Landing Page v3.0
 * Versión simplificada y comercialmente optimizada
 * Actualizado: 2026-07-25
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Activity, Users, Monitor, ClipboardCheck, Building2, GraduationCap,
  Shield, BarChart3, RefreshCw, Clock, ChevronRight, Play, Menu, X,
  Stethoscope, Heart, Brain, CheckCircle, ArrowRight, Sparkles,
  Building, Mail, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import CommercialDemoSection from '../components/landing/CommercialDemoSection';
import DemoRequestModal from '../components/landing/DemoRequestModal';
import PlansComparison from '../components/landing/PlansComparison';
import StudentCheckoutModal from '../components/landing/StudentCheckoutModal';

// Animaciones
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// ==================== COMPONENTES ====================

const Logo = ({ className = "h-10" }) => (
  <Link to="/" className="flex items-center gap-2">
    <div className="relative">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
        <Activity className="w-6 h-6 text-white" />
      </div>
    </div>
    <span className="text-xl font-bold text-white">
      Simu<span className="text-cyan-400">Clinic</span>
    </span>
  </Link>
);

// ==================== NAVEGACIÓN ====================
const Navigation = ({ isScrolled }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { label: 'Cómo funciona', href: '#como-funciona' },
    { label: 'Funcionalidades', href: '#funcionalidades' },
    { label: 'Soluciones', href: '#soluciones' },
    { label: 'Planes', href: '#planes' },
    { label: 'EUNACOM', href: '/eunacom' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              item.href.startsWith('/') ? (
                <Link key={item.label} to={item.href} className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
                  {item.label}
                </Link>
              ) : (
                <a key={item.label} href={item.href} className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
                  {item.label}
                </a>
              )
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button data-testid="nav-login" onClick={() => navigate('/login')} className="text-slate-300 hover:text-white text-sm font-medium px-4 py-2 transition-colors">
              Ingresar
            </button>
            <button 
              data-testid="nav-demo"
              onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all shadow-lg shadow-cyan-500/30"
            >
              Solicitar demo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white p-2">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-slate-800 rounded-xl p-4 mb-4 border border-slate-700"
          >
            {navItems.map((item) => (
              item.href.startsWith('/') ? (
                <Link key={item.label} to={item.href} onClick={() => setMobileOpen(false)} className="block py-3 text-slate-300 hover:text-white border-b border-slate-700 last:border-0">
                  {item.label}
                </Link>
              ) : (
                <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="block py-3 text-slate-300 hover:text-white border-b border-slate-700 last:border-0">
                  {item.label}
                </a>
              )
            ))}
            <div className="flex gap-2 mt-4">
              <button onClick={() => navigate('/login')} className="flex-1 text-white bg-slate-700 py-2.5 rounded-lg text-sm font-medium">
                Ingresar
              </button>
              <button onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })} className="flex-1 bg-cyan-500 text-white py-2.5 rounded-lg text-sm font-semibold">
                Demo
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// ==================== HERO ====================
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Simulación clínica digital
            </motion.div>
            
            <motion.h1 variants={fadeIn} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Entrena decisiones clínicas{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                antes de enfrentarlas en la vida real
              </span>
            </motion.h1>
            
            <motion.p variants={fadeIn} className="text-lg text-slate-300 mb-8 max-w-xl">
              SimuClinic permite crear, ejecutar y evaluar simulaciones clínicas interactivas en un entorno seguro, medible y disponible en todo momento.
            </motion.p>
            
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4">
              <button 
                data-testid="hero-cta-demo"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-cyan-500/30"
              >
                Solicitar demostración
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                data-testid="hero-cta-learn"
                onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl border border-white/20 transition-all"
              >
                <Play className="w-5 h-5" />
                Conocer SimuClinic
              </button>
            </motion.div>
          </motion.div>

          {/* Screenshot Real de la Plataforma */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-cyan-500/20 border border-slate-700/50">
              {/* Browser Bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-slate-900 rounded-md px-3 py-1 text-xs text-slate-400 text-center">
                    app.simuclinic.cl/escenarios
                  </div>
                </div>
              </div>
              
              {/* Screenshot Real */}
              <img 
                src="/images/platform-screenshot.jpg" 
                alt="SimuClinic - Plataforma de Simulación Clínica" 
                className="w-full h-auto"
              />

              {/* Badge flotante */}
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-lg flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Plataforma en vivo
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -z-10 -top-10 -right-10 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl" />
            <div className="absolute -z-10 -bottom-10 -left-10 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== PROBLEMA Y SOLUCIÓN ====================
const ProblemSection = () => {
  const problems = [
    {
      icon: Stethoscope,
      title: 'Práctica limitada',
      description: 'Los estudiantes necesitan más oportunidades para enfrentarse a situaciones clínicas antes de atender pacientes reales.'
    },
    {
      icon: ClipboardCheck,
      title: 'Evaluaciones poco estandarizadas',
      description: 'Muchas evaluaciones dependen de criterios subjetivos y no permiten revisar cada decisión tomada.'
    },
    {
      icon: RefreshCw,
      title: 'Dificultad para repetir escenarios',
      description: 'Los espacios, horarios y equipamientos físicos limitan la cantidad de veces que una experiencia puede practicarse.'
    }
  ];

  return (
    <section className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Más práctica, mejores decisiones y menos riesgos
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400 max-w-2xl mx-auto">
            Los desafíos actuales en la formación clínica
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-slate-600 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center mb-4">
                <problem.icon className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{problem.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-8"
        >
          <p className="text-xl text-white font-medium">
            SimuClinic reúne <span className="text-cyan-400">simulación, supervisión, evaluación y debriefing</span> en una sola plataforma.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== CÓMO FUNCIONA (3 PASOS) ====================
const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Preparar',
      description: 'El instructor selecciona o crea el escenario, establece los objetivos del briefing y asigna a los participantes.',
      icon: ClipboardCheck,
      color: 'from-violet-500 to-purple-600'
    },
    {
      number: '02',
      title: 'Simular',
      description: 'El estudiante toma decisiones clínicas mientras el instructor supervisa e interactúa en tiempo real.',
      icon: Activity,
      color: 'from-cyan-500 to-blue-600'
    },
    {
      number: '03',
      title: 'Evaluar',
      description: 'La plataforma registra el desempeño y permite realizar el debriefing, entregar retroalimentación y generar informes.',
      icon: BarChart3,
      color: 'from-emerald-500 to-teal-600'
    }
  ];

  return (
    <section id="como-funciona" className="py-20 bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Cómo funciona?
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400 max-w-2xl mx-auto">
            Un ciclo completo de aprendizaje clínico, desde los objetivos hasta la retroalimentación.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative"
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-slate-600 to-transparent" />
              )}

              <div className="bg-slate-900/50 border border-slate-700 rounded-2xl p-8 hover:border-slate-600 transition-all hover:-translate-y-1">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-sm font-bold text-slate-500 mb-2">PASO {step.number}</div>
                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Flow Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-4 mt-12"
        >
          <span className="text-violet-400 font-semibold">Preparar</span>
          <ChevronRight className="w-5 h-5 text-slate-500" />
          <span className="text-cyan-400 font-semibold">Simular</span>
          <ChevronRight className="w-5 h-5 text-slate-500" />
          <span className="text-emerald-400 font-semibold">Evaluar</span>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== FUNCIONALIDADES ====================
const FeaturesSection = () => {
  const features = [
    {
      icon: Monitor,
      title: 'Escenarios clínicos interactivos',
      description: 'Casos configurables que evolucionan según las decisiones del participante.',
      color: 'bg-cyan-500/20 text-cyan-400'
    },
    {
      icon: Heart,
      title: 'Monitor de signos vitales',
      description: 'Visualización dinámica del estado del paciente durante toda la simulación.',
      color: 'bg-red-500/20 text-red-400'
    },
    {
      icon: Users,
      title: 'Supervisión en tiempo real',
      description: 'El instructor puede observar a uno o varios estudiantes e interactuar con sus simulaciones.',
      color: 'bg-violet-500/20 text-violet-400'
    },
    {
      icon: ClipboardCheck,
      title: 'Evaluación y debriefing',
      description: 'Registro de acciones, tiempos, decisiones y oportunidades de mejora.',
      color: 'bg-emerald-500/20 text-emerald-400'
    },
    {
      icon: Building2,
      title: 'Gestión multiestablecimiento',
      description: 'Administración centralizada de instituciones, sedes, usuarios, escenarios y reportes.',
      color: 'bg-blue-500/20 text-blue-400'
    },
    {
      icon: GraduationCap,
      title: 'Preparación EUNACOM',
      description: 'Simulaciones individuales para médicos que desean practicar razonamiento y toma de decisiones clínicas.',
      color: 'bg-amber-500/20 text-amber-400'
    }
  ];

  return (
    <section id="funcionalidades" className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Funcionalidades principales
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400 max-w-2xl mx-auto">
            Todo lo que necesitas para gestionar simulaciones clínicas
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/30 transition-all cursor-default"
            >
              <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                <feature.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== POR QUÉ ELEGIR ====================
const WhyChooseSection = () => {
  const reasons = [
    {
      icon: Shield,
      title: 'Aprendizaje con menos riesgos',
      description: 'Permite practicar y cometer errores en un entorno seguro antes de enfrentarse a pacientes reales.'
    },
    {
      icon: BarChart3,
      title: 'Evaluación con información completa',
      description: 'Registra decisiones, tiempos, procedimientos y evolución del paciente para facilitar una retroalimentación objetiva.'
    },
    {
      icon: RefreshCw,
      title: 'Contenido revisado periódicamente',
      description: 'Los protocolos, documentos, medicamentos y escenarios pueden actualizarse de manera continua.'
    },
    {
      icon: Clock,
      title: 'Plataforma disponible 24/7',
      description: 'Permite acceder a las actividades desde cualquier lugar y en distintos horarios.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Por qué elegir SimuClinic?
          </motion.h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <reason.icon className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{reason.title}</h3>
              <p className="text-slate-400 text-sm">{reason.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-xl text-cyan-400 font-medium">
            Más oportunidades de practicar. Más información para evaluar. Menos riesgos para aprender.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== EQUIPO ====================
const TeamSection = () => {
  const areas = [
    {
      icon: Stethoscope,
      title: 'Experiencia clínica',
      description: 'Médicos y profesionales de enfermería participan en la revisión de escenarios, procedimientos y decisiones clínicas.'
    },
    {
      icon: GraduationCap,
      title: 'Formación y aprendizaje',
      description: 'Las funcionalidades se diseñan para apoyar la práctica, la evaluación y el debriefing.'
    },
    {
      icon: Monitor,
      title: 'Desarrollo tecnológico',
      description: 'Ingenieros y especialistas trabajan en el funcionamiento, estabilidad y evolución permanente de la plataforma.'
    }
  ];

  return (
    <section className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Experiencia clínica y tecnología trabajando en conjunto
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400 max-w-3xl mx-auto">
            SimuClinic es desarrollada y actualizada por un equipo multidisciplinario de médicos, enfermeras, ingenieros y profesionales con experiencia en salud, educación y tecnología.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {areas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <area.icon className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{area.title}</h3>
              <p className="text-slate-400 text-sm">{area.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-slate-400 italic max-w-3xl mx-auto"
        >
          &ldquo;Una plataforma creada por profesionales que comprenden los desafíos reales de enseñar, aprender y tomar decisiones clínicas.&rdquo;
        </motion.p>
      </div>
    </section>
  );
};

// ==================== SOLUCIONES ====================
const SolutionsSection = () => {
  const navigate = useNavigate();
  
  const solutions = [
    {
      title: 'SimuClinic Institucional',
      description: 'Para universidades, institutos, hospitales, clínicas y centros de simulación.',
      features: ['Gestión de alumnos e instructores', 'Escenarios y evaluaciones', 'Supervisión en tiempo real', 'Reportes institucionales'],
      cta: 'Conocer planes institucionales',
      action: () => document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' }),
      gradient: 'from-cyan-500 to-blue-600',
      icon: Building
    },
    {
      title: 'SimuClinic EUNACOM',
      description: 'Para médicos que desean practicar individualmente mediante casos clínicos interactivos.',
      features: ['Cuenta personal', 'Simulaciones limitadas', 'Casos por especialidad', 'Seguimiento de progreso'],
      cta: 'Prepararme para EUNACOM',
      action: () => navigate('/eunacom'),
      gradient: 'from-emerald-500 to-teal-600',
      icon: GraduationCap
    },
    {
      title: 'SimuClinic Multiestablecimiento',
      description: 'Para redes académicas u hospitalarias que necesitan administrar múltiples sedes.',
      features: ['Administración centralizada', 'Múltiples establecimientos', 'Configuración por sede', 'Reportes consolidados'],
      cta: 'Solicitar cotización',
      action: () => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }),
      gradient: 'from-violet-500 to-purple-600',
      icon: Building2
    }
  ];

  return (
    <section id="soluciones" className="py-20 bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Soluciones disponibles
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400">
            Encuentra la opción que mejor se adapte a tus necesidades
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden hover:border-slate-600 transition-all"
            >
              <div className={`h-2 bg-gradient-to-r ${solution.gradient}`} />
              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${solution.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                  <solution.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{solution.title}</h3>
                <p className="text-slate-400 text-sm mb-4">{solution.description}</p>
                <ul className="space-y-2 mb-6">
                  {solution.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={solution.action}
                  className={`w-full bg-gradient-to-r ${solution.gradient} text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  {solution.cta}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PLANES EN UF ====================
const PricingSection = ({ onRequestDemo, onStudentCheckout }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [ufConfig, setUfConfig] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchUfConfig();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plans/public`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUfConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/uf/current`);
      if (response.ok) {
        const data = await response.json();
        setUfConfig(data);
      }
    } catch (error) {
      console.error('Error fetching UF config:', error);
    }
  };

  // Formatear precio en UF con formato chileno (6,20 UF)
  const formatUF = (value, decimals = 2) => {
    if (value === null || value === undefined) return null;
    const formatted = value.toFixed(decimals).replace('.', ',');
    return `${formatted} UF`;
  };

  // Formatear precio CLP referencial
  const formatCLP = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Obtener precio en UF según el ciclo
  const getPriceUF = (plan) => {
    if (plan.requiere_cotizacion) return null;
    const pricing = plan.pricing || {};
    
    if (billingCycle === 'annual') {
      // Precio anual dividido entre 12 para mostrar equivalente mensual
      const precioAnualUF = pricing.precio_anual_uf || (pricing.precio_mensual_uf * 12 * 0.8);
      return precioAnualUF / 12;
    }
    return pricing.precio_mensual_uf || 0;
  };

  // Obtener precio total anual en UF
  const getAnnualTotalUF = (plan) => {
    const pricing = plan.pricing || {};
    return pricing.precio_anual_uf || (pricing.precio_mensual_uf * 12 * 0.8);
  };

  // Calcular equivalencia en CLP
  const getEquivalenciaCLP = (ufValue) => {
    if (!ufConfig || !ufValue) return null;
    return Math.round(ufValue * ufConfig.valor);
  };

  if (loading) {
    return (
      <section id="planes" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-700 rounded w-48 mx-auto mb-4" />
            <div className="h-4 bg-slate-700 rounded w-64 mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="planes" className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2 variants={fadeIn} className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Planes institucionales
          </motion.h2>
          <motion.p variants={fadeIn} className="text-slate-400 mb-8">
            Elige el plan que mejor se adapte a tu institución. Precios en UF, facturación anual.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {plans.slice(0, 5).map((plan, index) => {
            const isCLPPlan = plan.pricing?.moneda_principal === 'CLP';
            const precioMensualCLP = plan.pricing?.precio_mensual_clp;
            const simsIncluidas = plan.pricing?.simulaciones_incluidas_mes;
            return (
            <motion.div
              key={plan.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-slate-800/50 border rounded-2xl overflow-hidden ${
                plan.es_recomendado ? 'border-cyan-500 shadow-lg shadow-cyan-500/20' : 'border-slate-700'
              }`}
            >
              {plan.es_recomendado && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold py-1.5 text-center">
                  Más recomendado
                </div>
              )}
              {plan.etiqueta_destacada && !plan.es_recomendado && (
                <div className="absolute top-0 left-0 right-0 text-white text-xs font-bold py-1.5 text-center" style={{ backgroundColor: plan.color || '#6366F1' }}>
                  {plan.etiqueta_destacada}
                </div>
              )}

              <div className={`p-6 ${plan.es_recomendado || plan.etiqueta_destacada ? 'pt-10' : ''}`}>
                <h3 className="text-xl font-bold text-white mb-1">{plan.nombre}</h3>
                <p className="text-slate-400 text-sm mb-4">{plan.publico_objetivo || plan.descripcion_breve}</p>

                {isCLPPlan ? (
                  // ============ Estudiante: precio en CLP directo ============
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">
                        {formatCLP(precioMensualCLP || 0)}
                      </span>
                    </div>
                    <span className="text-slate-400 text-sm">/mes · IVA incluido</span>
                    {simsIncluidas && (
                      <p className="text-cyan-400 text-xs mt-1.5">
                        Incluye {simsIncluidas} simulaciones/mes
                      </p>
                    )}
                    {plan.pricing?.precio_semestral_clp && (
                      <p className="text-emerald-400 text-xs mt-0.5">
                        También en packs 3 y 6 meses (ahorra hasta {formatCLP(59500)})
                      </p>
                    )}
                  </div>
                ) : plan.requiere_cotizacion ? (
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      {plan.pricing?.mostrar_desde && (
                        <span className="text-slate-400 text-sm">Desde</span>
                      )}
                      <span className="text-2xl font-bold text-white">
                        {plan.pricing?.precio_anual_uf ? formatUF(plan.pricing.precio_anual_uf) : 'Cotización'}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      /año + IVA
                      {plan.pricing?.implementacion_uf > 0 && (
                        <span className="block text-xs mt-0.5">
                          + {formatUF(plan.pricing.implementacion_uf)} implementación
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">
                        {formatUF(plan.pricing?.precio_anual_uf || 0)}
                      </span>
                      <span className="text-slate-400 text-sm">+ IVA</span>
                    </div>
                    <span className="text-slate-400 text-sm">/año · facturación anual</span>
                    {plan.pricing?.implementacion_uf > 0 && (
                      <p className="text-slate-400 text-xs mt-1">
                        + {formatUF(plan.pricing.implementacion_uf)} implementación (una vez)
                      </p>
                    )}
                    {plan.pricing?.compromiso_3a_precio_anual_uf && (
                      <p className="text-emerald-400 text-xs mt-1">
                        Contrato 3 años: {formatUF(plan.pricing.compromiso_3a_precio_anual_uf)}/año
                      </p>
                    )}
                    {ufConfig?.mostrar_equivalencia_clp && (
                      <p className="text-slate-500 text-xs mt-0.5">
                        ≈ {formatCLP(getEquivalenciaCLP(plan.pricing?.precio_anual_uf))}/año ref.
                      </p>
                    )}
                  </div>
                )}

                <ul className="space-y-2 mb-6">
                  {(plan.features_destacadas || plan.features || []).slice(0, 5).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-cta-${plan.codigo}`}
                  onClick={() => {
                    if (plan.codigo === 'estudiante') {
                      onStudentCheckout?.();
                    } else {
                      onRequestDemo?.();
                    }
                  }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.es_recomendado
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:opacity-90'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  {plan.texto_boton || (plan.requiere_cotizacion ? 'Solicitar cotización' : 'Comenzar')}
                </button>
              </div>
            </motion.div>
          );
          })}
        </div>

        {/* Tabla comparativa expandible */}
        <PlansComparison plans={plans.slice(0, 5)} />

        {/* Nota sobre precios en UF */}
        <motion.p 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }}
          className="text-center text-slate-500 text-xs mt-8"
        >
          Valores netos expresados en UF (Unidad de Fomento). No incluyen IVA.
          {ufConfig?.mostrar_equivalencia_clp && (
            <span className="block mt-1">
              {ufConfig.texto_equivalencia}. Valor UF: {ufConfig.valor_formateado}
            </span>
          )}
        </motion.p>
      </div>
    </section>
  );
};

// ==================== LLAMADO FINAL ====================
const CTASection = ({ onRequestDemo }) => {
  return (
    <section id="contacto" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Convierte cada simulación en una oportunidad de aprendizaje
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Prepara, supervisa y evalúa experiencias clínicas desde una plataforma creada para instituciones y profesionales de la salud.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={onRequestDemo}
              data-testid="cta-request-demo-btn"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/30"
            >
              <Mail className="w-5 h-5" />
              Solicitar demostración
            </button>
            <button
              onClick={() => document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl border border-white/20 transition-all"
            >
              Ver planes
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
            <a href="mailto:contacto@simuclinic.cl" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="w-4 h-4" />
              contacto@simuclinic.cl
            </a>
            <a href="https://wa.me/56912345678" className="flex items-center gap-2 hover:text-white transition-colors">
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer className="bg-slate-900 border-t border-slate-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Logo />
            <p className="text-slate-400 text-sm mt-4">
              Plataforma de simulación clínica digital para la formación de profesionales de la salud.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Producto</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#como-funciona" className="text-slate-400 hover:text-white transition-colors">Cómo funciona</a></li>
              <li><a href="#funcionalidades" className="text-slate-400 hover:text-white transition-colors">Funcionalidades</a></li>
              <li><a href="#planes" className="text-slate-400 hover:text-white transition-colors">Planes</a></li>
              <li><Link to="/eunacom" className="text-slate-400 hover:text-white transition-colors">EUNACOM</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Soluciones</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#soluciones" className="text-slate-400 hover:text-white transition-colors">Institucional</a></li>
              <li><Link to="/eunacom" className="text-slate-400 hover:text-white transition-colors">EUNACOM Individual</Link></li>
              <li><a href="#contacto" className="text-slate-400 hover:text-white transition-colors">Multiestablecimiento</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">Contacto</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:contacto@simuclinic.cl" className="text-slate-400 hover:text-white transition-colors">contacto@simuclinic.cl</a></li>
              <li><a href="#contacto" className="text-slate-400 hover:text-white transition-colors">Solicitar demo</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 SimuClinic. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Términos</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Privacidad</a>
            <span className="text-slate-600">v3.0.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== PÁGINA PRINCIPAL ====================
const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [demoFormOpen, setDemoFormOpen] = useState(false);
  const [studentCheckoutOpen, setStudentCheckoutOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'solicitar') {
      setDemoFormOpen(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900">
      <Navigation isScrolled={isScrolled} />
      <HeroSection />
      <CommercialDemoSection onRequestDemo={() => setDemoFormOpen(true)} />
      <ProblemSection />
      <HowItWorksSection />
      <FeaturesSection />
      <WhyChooseSection />
      <TeamSection />
      <SolutionsSection />
      <PricingSection
        onRequestDemo={() => setDemoFormOpen(true)}
        onStudentCheckout={() => setStudentCheckoutOpen(true)}
      />
      <CTASection onRequestDemo={() => setDemoFormOpen(true)} />
      <Footer />
      <DemoRequestModal open={demoFormOpen} onClose={() => setDemoFormOpen(false)} />
      <StudentCheckoutModal open={studentCheckoutOpen} onClose={() => setStudentCheckoutOpen(false)} />
    </div>
  );
};

export default LandingPage;
